"use client";

import { useEffect } from "react";

/**
 * Android hardware back button — 2026 Capacitor 7 best practice
 * (Capacitor docs App Plugin `backButton` + Capgo UX blog 2026-06-18)
 *
 * Without this, Android users get a jarring default: either the app closes
 * immediately or back navigation feels unpredictable because the WebView history
 * and native back stack aren't coordinated. Vortixy is a single-page chat workspace,
 * so we implement the comfortable pattern:
 * - If WebView can go back (history.length >1 or path !== /chat) → history.back()
 * - Else → minimize to background (not exit) so drafts/tasks aren't lost.
 * listening disables the default, so we must call manually per docs.
 *
 * Also adds light haptic feedback on back when available (@capacitor/haptics) for
 * tactile comfort — 2026 mobile UX playbook: micro-interactions improve perceived quality.
 */
export function NativeBackHandler() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (!cap?.isNativePlatform?.()) return;

    let remove: (() => void) | null = null;
    let cancelled = false;

    void import("@capacitor/app").then(async ({ App }) => {
      if (cancelled) return;
      const handle = await App.addListener("backButton", async ({ canGoBack }) => {
        // Subtle haptic for comfort when available
        try {
          const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
          await Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
        } catch {
          // haptics not installed or not available — ignore
        }

        // If user is not on main chat route, go back within WebView
        const path = window.location.pathname;
        const isHome =
          path === "/" || path === "/chat" || path === "/es/chat" || path.endsWith("/chat");
        // canGoBack comes from native WebView stack; combine with pathname guard
        if (canGoBack && !isHome) {
          window.history.back();
          return;
        }
        // On home/chat with no history — minimize rather than abruptly exit
        // (more comfortable: user returns via recents, drafts preserved)
        try {
          await App.minimizeApp();
        } catch {
          // Fallback: exit if minimize not available (older Capacitor)
          await App.exitApp().catch(() => {});
        }
      });
      remove = handle.remove;
    });

    return () => {
      cancelled = true;
      remove?.();
    };
  }, []);

  return null;
}
